import React, { useState, useEffect } from "react";
import "./Table.css";
import ActionDropdown from "../ActionDropdown/ActionDropdown";
import { getContrastColor } from "../../utils/GetContrastColor";
import { LoadingSpinner } from "../LoadingSpinner/LoadingSpinner";
import { formatDateTime } from "../../utils/dateUtils";

export default function Table({ 
  table_columns,
  rows,
  setRowsId,
  onRowClick,
  toggleModel,
  buttons = [],
  setSelectedRowId = () => {},
  setCurrentPage,
  currentPage,
  hasMorePages,
  onFieldUpdate,
  showPagination = true,
  isLoading = false,
  loadingMessage = "Loading data...",
  loadingProgress = 0,
}) {
  console.log("TripTable Component Rendered");

  const [openRowId, setOpenRowsId] = useState(null); 
  const [selectedRows, setSelectedRows] = useState([]);

  useEffect(() => {
    if (setRowsId) setRowsId(openRowId);
  }, [openRowId, setRowsId]);

  useEffect(() => {
    setSelectedRowId(selectedRows);
  }, [selectedRows, setSelectedRowId]);


  const getNextStatus = (status) => {
    const statuses = ["approved", "rejected"];
    let idx = -1;
    if(status !== "pending") idx = statuses.indexOf(status);
    return statuses[(idx + 1) % statuses.length];
  };

  const handlePageChange = (direction) => {

    if (direction === "next") {
      setCurrentPage((prev) => (prev + 1));
    } else if (direction === "prev") {
      setCurrentPage((prev) => (prev - 1 > 0 ? prev - 1 : prev));
    }
  };

  const handleSelectAll = () => {
    if (selectedRows.length === rows.length) {
      setSelectedRows([]);
    } else {
      const rowsOnCurrentPage = rows.map(row => row.id);
      setSelectedRows(rowsOnCurrentPage);
    }
  };

  const handleSelectRow = (rowId) => {
    setSelectedRows((prevSelectedRows) => {
      if (prevSelectedRows.includes(rowId)) {
        return prevSelectedRows.filter(id => id !== rowId);
      }
      return [...prevSelectedRows, rowId];
    });
  };

  const handleRowClick = (row) => {
    if (onRowClick) onRowClick(row);
    if (toggleModel) toggleModel();
  };

  const selectColumn = table_columns.find(col => col.field === 'select');

  const getStatusColor = (status) => {
    switch (status) {
      case "DISPATCHED":
      case "approved":
        return "green"; 
      case "Cancelled":
      case "rejected":
        return "red"; 
      case "Pending":
        return "grey";
      case "Completed":
        return "blue";
      default:
        return "grey";
    }
  };

  return (
    <>
      <div className="data-table">
        <div className="table-container">
          {/* Table Header - Fixed */}
          <div className="table-header">
            <table>
              <thead>
                <tr>
                  {selectColumn && (
                    <th>
                      <input 
                        type="checkbox" 
                        checked={rows.length > 0 && selectedRows.length === rows.length}
                        onChange={handleSelectAll}
                      />
                    </th>
                  )}
                  {table_columns.map((col) => (
                    col.field !== 'select' && <th key={col.field}>{col.headerName}</th>
                  ))}
                  <th>Actions</th>
                </tr>
              </thead>
            </table>
          </div>
          
          {/* Table Body - Scrollable */}
          <div className="table-body-container">
            <table>
              <tbody>
                {isLoading ? (
                  <tr className="loading-row" style={{ width: "100%", borderBottom: "none" }}>
                    <td colSpan={table_columns.length + 2} className="loading-cell" style={{ width: "100%" }}>
                      <div className="table-body-loading" style={{ width: "100%" }}>
                        <LoadingSpinner 
                          overlay={false}
                          message={loadingMessage}
                          progress={loadingProgress}
                          showProgress={true}
                        />
                      </div>
                    </td>
                  </tr>
                ) : (
                  (rows || []).map((row) => (
                    <tr key={row.id}>
                      {(table_columns || []).map((col) => (
                        <td key={col.field} onClick={col.field === 'select' ? null : () => handleRowClick(row.id)}>
                          {col.field === 'select' ? (
                            <input
                              type="checkbox"
                              checked={selectedRows.includes(row.id)}
                              onChange={() => handleSelectRow(row.id)}
                            />
                          ) : col.renderCell ? (
                            col.renderCell({ row })  
                          ) : col.field === "color" && row[col.field] ? (
                            <span
                              style={{
                                backgroundColor: `rgba(${
                                  row[col.field] === 'red' ? '255,0,0' :
                                  row[col.field] === 'green' ? '0,255,0' :
                                  row[col.field] === 'blue' ? '0,0,255' :
                                  row[col.field] === 'yellow' ? '255,255,0' :
                                  row[col.field] === 'orange' ? '255,165,0' :
                                  '0,0,0'
                                }, 0.2)`,
                                color: row[col.field] === 'yellow' ? '#666600' :
                                      row[col.field] === 'green' ? '#006600' :
                                      row[col.field] === 'orange' ? '#cc5500' :
                                      row[col.field],
                                padding: "5px 10px",
                                borderRadius: "12px",
                                fontSize: "0.9em",
                                fontWeight: "bold",
                              }}
                            >
                              {row[col.field]}
                            </span>
                          ) : col.field === "status" && row[col.field] ? (
                            <span
                            onClick={() => {
                              const nextStatus = getNextStatus(row[col.field]);
                              onFieldUpdate?.({ rowId: row.id, field: col.field, newValue: nextStatus });
                            }}
                              style={{
                                backgroundColor: getStatusColor(row[col.field]),
                                color: "#fff",
                                padding: "5px 10px",
                                borderRadius: "12px",
                                fontSize: "0.9em",
                                fontWeight: "bold",
                                cursor: "pointer",
                              }}
                              title="Click to change status"
                            >
                              {row[col.field]}
                            </span>
                          ) : col.type === "datetime-local" ? (
                            row[col.field] ? formatDateTime(row[col.field], "datetime-local") : "N/A"
                          ) : (
                            typeof row[col.field] === 'boolean'
                              ? row[col.field] ? "Yes" : "No"
                              : row[col.field]
                          )}
                        </td>
                      ))}
                      <td>
                        <ActionDropdown
                          rowId={row.id} 
                          isOpen={openRowId === row.id}
                          setOpenRowId={setOpenRowsId}
                          buttons={row.actionButtons || buttons}
                        />
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          
          {/* Pagination - Fixed */}
          {showPagination && (
          <div style={{ 
            position: 'sticky',
            bottom: 0,
            backgroundColor: 'var(--background-primary)',
            padding: 'var(--spacing-sm)',
            borderBottomRightRadius: 'var(--border-radius-md)',
            borderBottomLeftRadius: 'var(--border-radius-md)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            gap: 'var(--spacing-sm)',
            borderTop: '1px solid var(--border-color)'
          }}>
            <span style={{ marginRight: '10px' }}>
              Page {currentPage}
            </span>
            <button 
              style={{
                padding: '4px 8px',
                border: 'none',
                backgroundColor: 'transparent',
                cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                color: currentPage === 1 ? 'var(--text-secondary)' : 'var(--text-primary)',
                fontSize: '16px'
              }}
              onClick={() => handlePageChange("prev")} 
              disabled={currentPage === 1}
            >
              &lt;
            </button>
            <button 
              style={{
                padding: '4px 8px',
                border: 'none',
                backgroundColor: 'transparent',
                cursor: !hasMorePages ? 'not-allowed' : 'pointer',
                color: !hasMorePages ? 'var(--text-secondary)' : 'var(--text-primary)',
                fontSize: '16px'
              }}
              onClick={() => handlePageChange("next")} 
              disabled={!hasMorePages}
            >
              &gt;
            </button>
          </div>
          )}
        </div>
      </div>
    </>
  );
}
